import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GlobalLanguageSelector from './GlobalLanguageSelector';
import { 
  LayoutDashboard, 
  Upload, 
  Bot, 
  CheckSquare, 
  MessageSquare, 
  Users, 
  LogOut,
  Database,
  TrendingUp,
  Moon,
  Sun,
  Building2,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  FileText,
  LineChart,
  Settings,
  Activity,
  Shield,
  UserCog,
  Target,
  Footprints,
  Package,
  PieChart,
  Brain,
  Home,
  FolderInput,
  Cpu,
  MessageCircle,
  Store,
  Truck,
  RotateCcw,
  Flame,
  Bell,
  ClipboardList,
  Scale
} from 'lucide-react';
import { Button } from './ui/button';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Dark mode state (persisted in localStorage)
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Impersonation state - check localStorage on mount and route changes
  const [impersonatedTenant, setImpersonatedTenant] = useState(() => {
    const saved = localStorage.getItem('impersonatedTenant');
    return saved ? JSON.parse(saved) : null;
  });

  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    'Home': true,
    'Data Management': false,
    'AI Control Center': false,
    'Business Assistant': true,
    'Agent Dashboards': false,
    'Forecasts': false,
    'Business Performance': false,
    'Settings': false,
    'Platform Administration': false,
    'Company Administration': false,
  });

  // Persist dark mode changes
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Re-check impersonation state on route changes
  useEffect(() => {
    const saved = localStorage.getItem('impersonatedTenant');
    setImpersonatedTenant(saved ? JSON.parse(saved) : null);
  }, [location.pathname]);

  const handleExitImpersonation = () => {
    localStorage.removeItem('impersonatedTenant');
    setImpersonatedTenant(null);
    navigate('/superadmin/monitoring');
  };

  // Role-based access configuration
  const OPERATIONAL_ROLES = ['superadmin', 'companyadmin', 'admin', 'manager', 'analyst', 'user', 'company_admin'];
  const ADMIN_ROLES = ['admin', 'company_admin', 'superadmin'];
  const SUPERADMIN_ROLES = ['superadmin'];

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // New menu structure as specified
  const menuStructure = [
    // === PLATFORM ADMINISTRATION (SuperAdmin Only) ===
    {
      section: 'Platform Administration',
      icon: Shield,
      roles: SUPERADMIN_ROLES,
      collapsible: true,
      items: [
        { icon: Activity, label: 'Platform Monitoring', path: '/superadmin/monitoring', testId: 'nav-platform-monitoring' },
        { icon: Building2, label: 'Company Management', path: '/superadmin/companies', testId: 'nav-manage-companies' },
        { icon: Users, label: 'Global Users', path: '/superadmin/users', testId: 'nav-global-users' },
      ]
    },
    
    // === COMPANY ADMINISTRATION (Admin Only) ===
    {
      section: 'Company Administration',
      icon: UserCog,
      roles: ADMIN_ROLES,
      collapsible: true,
      items: [
        { icon: Users, label: 'User Management', path: '/admin/users', testId: 'nav-admin-users' },
        { icon: Settings, label: 'Company Settings', path: '/admin/settings', testId: 'nav-company-settings' },
        { icon: FileText, label: 'Audit Log', path: '/admin/audit-log', testId: 'nav-audit-log' },
        { icon: Bell, label: 'Alert Settings', path: '/admin/alert-settings', testId: 'nav-alert-settings' },
      ]
    },

    // === HOME ===
    {
      section: 'Home',
      icon: Home,
      roles: OPERATIONAL_ROLES,
      collapsible: false,
      items: [
        { icon: LayoutDashboard, label: 'Overview Dashboard', path: '/dashboard', testId: 'nav-dashboard' },
      ]
    },

    // === DATA MANAGEMENT ===
    {
      section: 'Data Management',
      icon: FolderInput,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: Upload, label: 'Data Upload', path: '/upload-res', testId: 'nav-upload' },
        { icon: BarChart3, label: 'Upload History', path: '/upload-history', testId: 'nav-upload-history' },
      ]
    },

    // === AI CONTROL CENTER ===
    {
      section: 'AI Control Center',
      icon: Cpu,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: Bot, label: 'AI Agents', path: '/agents', testId: 'nav-agents' },
        { icon: CheckSquare, label: 'Agent Results', path: '/agent-results', testId: 'nav-agent-results' },
        { icon: Lightbulb, label: 'Agent Recommendations', path: '/agent-recommendations', testId: 'nav-agent-recommendations' },
        { icon: ClipboardList, label: 'Task Board', path: '/tasks', testId: 'nav-tasks' },
      ]
    },

    // === BUSINESS ASSISTANT ===
    {
      section: 'Business Assistant',
      icon: MessageCircle,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: MessageSquare, label: 'REASONBOT', path: '/chatbot', testId: 'nav-chatbot', highlight: true },
        { icon: FileText, label: 'Executive Summary', path: '/executive-summary', testId: 'nav-executive-summary' },
      ]
    },

    // === AGENT DASHBOARDS ===
    {
      section: 'Agent Dashboards',
      icon: PieChart,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: Brain, label: 'Executive Brain', path: '/dashboard/executive-brain', testId: 'nav-executive-brain-dashboard' },
        { icon: Package, label: 'StockSense', path: '/dashboard/stock-sense', testId: 'nav-stock-sense-dashboard' },
        { icon: TrendingUp, label: 'SalesIQ', path: '/dashboard/sales-iq', testId: 'nav-sales-iq-dashboard' },
        { icon: Users, label: 'Customer360', path: '/dashboard/customer-360', testId: 'nav-customer-360-dashboard' },
        { icon: PieChart, label: 'ProductMix', path: '/dashboard/product-mix', testId: 'nav-product-mix-dashboard' },
        { icon: Truck, label: 'VendorLink', path: '/dashboard/vendor-link', testId: 'nav-vendor-link-dashboard' },
        { icon: Store, label: 'StorePulse', path: '/dashboard/store-pulse', testId: 'nav-store-pulse-dashboard' },
        { icon: Flame, label: 'PromoIQ', path: '/dashboard/promo-iq', testId: 'nav-promo-iq-dashboard' },
        { icon: RotateCcw, label: 'ReturnIQ', path: '/dashboard/return-iq', testId: 'nav-return-iq-dashboard' },
        { icon: LineChart, label: 'DemandFlow', path: '/dashboard/demand-flow', testId: 'nav-demand-flow-dashboard' },
        { icon: Scale, label: 'Commercial Intervention', path: '/dashboard/commercial-intervention', testId: 'nav-commercial-intervention-dashboard' },
      ]
    },

    // === FORECASTS ===
    {
      section: 'Forecasts',
      icon: LineChart,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: LineChart, label: 'Forecasts', path: '/forecasts', testId: 'nav-forecasts', highlight: true },
        { icon: BarChart3, label: 'Forecast Comparison', path: '/forecast-comparison', testId: 'nav-forecast-comparison' },
      ]
    },

    // === BUSINESS PERFORMANCE ===
    {
      section: 'Business Performance',
      icon: BarChart3,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: Footprints, label: 'Footfall vs Sales', path: '/footfall-vs-sales', testId: 'nav-footfall-vs-sales' },
        { icon: Target, label: 'Target vs Achievement', path: '/target-vs-achievement', testId: 'nav-target-vs-achievement' },
      ]
    },

    // === SETTINGS ===
    {
      section: 'Settings',
      icon: Settings,
      roles: OPERATIONAL_ROLES,
      collapsible: true,
      items: [
        { icon: Users, label: 'Manage Profile', path: '/manage-profile', testId: 'nav-manage-profile' },
      ]
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const userRole = user?.role?.toLowerCase();

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
      {/* Fixed Sidebar - 260px width */}
      <aside 
        className="w-[260px] bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col shadow-lg fixed left-0 top-0 h-screen"
        data-testid="sidebar"
      >
        {/* Sidebar Header with Logo - Clickable to navigate home */}
        <div 
          className="h-20 flex items-center px-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          onClick={() => navigate('/dashboard')}
          data-testid="header-logo"
        >
          <div className="flex items-center space-x-3">
            <img 
              src="/reason-logo.jpg" 
              alt="REASON" 
              className="h-12 w-auto object-contain"
            />
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">
                Reason <span className="text-blue-600">RetailIQ</span>
              </h1>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">Enterprise Retail Intelligence</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {menuStructure.map((section, sectionIndex) => {
            // Check section-level role access
            if (section.roles && !section.roles.includes(userRole)) {
              return null;
            }

            // Filter items within section based on role
            const visibleItems = section.items || [];
            if (visibleItems.length === 0) return null;

            const SectionIcon = section.icon;
            const isExpanded = expandedSections[section.section];
            const hasActiveItem = visibleItems.some(item => isActive(item.path));

            return (
              <div key={sectionIndex} className="space-y-0.5">
                {/* Section Header - Collapsible */}
                {section.collapsible ? (
                  <button
                    onClick={() => toggleSection(section.section)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                      text-xs font-semibold uppercase tracking-wider
                      transition-all duration-200
                      ${hasActiveItem 
                        ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <SectionIcon className="h-4 w-4 mr-2" />
                      {section.section}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                ) : (
                  <div className="flex items-center px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <SectionIcon className="h-4 w-4 mr-2" />
                    {section.section}
                  </div>
                )}
                
                {/* Section Items - Show if expanded or not collapsible */}
                {(isExpanded || !section.collapsible) && (
                  <div className="ml-2 space-y-0.5">
                    {visibleItems.map((item, itemIndex) => {
                      const ItemIcon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <button
                          key={itemIndex}
                          data-testid={item.testId}
                          onClick={() => navigate(item.path)}
                          className={`
                            w-full flex items-center space-x-3 px-3 py-2 rounded-lg
                            transition-all duration-200 text-left text-sm
                            ${active
                              ? 'bg-blue-600 text-white font-medium shadow-md shadow-blue-600/25'
                              : item.highlight
                                ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium'
                                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }
                          `}
                        >
                          <ItemIcon 
                            className={`h-4 w-4 flex-shrink-0 ${
                              active 
                                ? 'text-white' 
                                : item.highlight 
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-slate-500 dark:text-slate-400'
                            }`} 
                          />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Profile & Settings at Bottom */}
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
          {/* Dark Mode Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDarkMode(!darkMode)}
            className="w-full justify-start text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 h-9"
          >
            {darkMode ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>

          {/* User Info */}
          <div className="flex items-center space-x-3 px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-medium">
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                {user?.email}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                {user?.role === 'company_admin' ? 'Admin' : user?.role}
              </p>
            </div>
          </div>

          {/* Logout Button */}
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full justify-start text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 h-9"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Footer - Made in India */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <span className="text-[10px] text-slate-500 dark:text-slate-400">🇮🇳 Made in India</span>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <span className="text-[10px] text-blue-600 dark:text-blue-400">Startup India</span>
          </div>
          <p className="text-[9px] text-slate-400 dark:text-slate-500 text-center">
            ©2025 Reason Analytics Softech Pvt Ltd.
          </p>
        </div>
      </aside>

      {/* Main Content Area - Offset by sidebar width (260px) */}
      <div className="flex-1 flex flex-col ml-[260px]">
        {/* Impersonation Banner - Only visible when superadmin is viewing another tenant */}
        {user?.role === 'superadmin' && impersonatedTenant && (
          <div className="bg-amber-400 text-amber-900 px-4 py-2 flex items-center justify-between shadow-md" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="font-medium">
                You are viewing tenant <strong>{impersonatedTenant.companyName}</strong> as SuperAdmin
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/90 hover:bg-white text-amber-900 border-amber-600"
              onClick={handleExitImpersonation}
              data-testid="exit-impersonation-btn"
            >
              Exit View Mode
            </Button>
          </div>
        )}

        {/* Top Header with Logo */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center space-x-4">
            {/* Small logo in header */}
            <img 
              src="/reason-logo.jpg" 
              alt="REASON" 
              className="h-8 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="header-mini-logo"
            />
            <div className="h-6 w-px bg-slate-300 dark:bg-slate-600"></div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {/* Dynamic page title based on location */}
              {location.pathname === '/dashboard' ? 'Overview Dashboard' :
               location.pathname === '/chatbot' ? 'REASONBOT' :
               location.pathname.includes('/dashboard/') ? 
                 location.pathname.split('/').pop().split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('') + ' Dashboard' :
               'Reason RetailIQ'}
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {/* Global Language Selector - Enterprise Grade */}
            <GlobalLanguageSelector />
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-600"></div>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {user?.companyName || 'Reason Retail Group'}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6 bg-slate-50 dark:bg-slate-900">
          <Outlet />
        </main>

        {/* Main Content Footer */}
        <footer className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-3">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-4">
              <span>©2025 Reason Analytics Softech Pvt Ltd.</span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span className="flex items-center gap-1">
                🇮🇳 Made in India
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span className="text-blue-600 dark:text-blue-400">Startup India | DPIIT Recognized</span>
            </div>
            <div>
              <span>Reason RetailIQ v2.0</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
